import React, {useMemo} from 'react';


import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {
    UnsubscribeFromLcdCreditAnalysisTool,
    UpdateUserAccountTool,
    UpdateUserNewsletterTool, UpdateUserStateTool,
} from 'src/modules/bulk-update';
import {SubscribeToLcdCreditAnalysisTool} from 'src/modules/bulk-update/update-lcd-credit-analysis';
import {AssignToGroupTool, UnassignFromGroupTool} from 'src/modules/bulk-update/update-user-group';
import {allSelectedUsersAccountsSelector} from 'src/modules/user-search-result';

import {useShown} from '@pb/kit-hooks-use-shown';

import {ToolsMenu} from './tools-menu';
import {ToolsRow} from './tools-row';

import type {AppState} from 'src/store/typedef';

type Props = {
    ids: number[],
    onDone: () => void,
};

export const UsersAdvancedSearchBulkActions = ({ids, onDone}: Props) => {
    const {hasAccess} = useAdminAccessRight();

    const {
        shown: assignToGroupShown,
        onClick: openAssignToGroup,
        onClose: closeAssignToGroup,
    } = useShown();
    const {
        shown: dischargeToGroupShown,
        onClick: openDischargeToGroup,
        onClose: closeDischargeToGroup,
    } = useShown();
    const {
        shown: updateUserStateToolShown,
        onClick: openUpdateUserStateTool,
        onClose: closeUpdateUserStateTool,
    } = useShown();
    const {
        shown: updateUserNewsletterToolShown,
        onClick: openUpdateUserNewsletterTool,
        onClose: closeUpdateUserNewsletterTool,
    } = useShown();
    const {
        shown: updateUserAccountToolShown,
        onClick: openUpdateUserAccountTool,
        onClose: closeUpdateUserAccountTool,
    } = useShown();

    const {
        shown: subscribeToLcdAnalysisToolShown,
        onClick: openSubscribeToLcdAnalysisTool,
        onClose: closeSubscribeToLcdAnalysisTool,
    } = useShown();

    const {
        shown: unsubscribeFromLcdAnalysisToolShown,
        onClick: openUnsubscribeFromLcdAnalysisTool,
        onClose: closeUnsubscribeFromLcdAnalysisTool,
    } = useShown();

    const showUpdateGroup = useMemo(() => (
        hasAccess(ACCESS_RIGHTS.BULK_ACCOUNT_GROUP_UPDATE)
    ), [hasAccess]);
    const showUpdateUserState = useMemo(() => (
        hasAccess(ACCESS_RIGHTS.BULK_USER_STATE_UPDATE)
    ), [hasAccess]);
    const showUpdateUserNewsletter = useMemo(() => (
        hasAccess(ACCESS_RIGHTS.BULK_NEWSLETTER_SUBSCRIPTION_UPDATE)
    ), [hasAccess]);
    const showUpdateUserAccount = useMemo(() => (
        hasAccess(ACCESS_RIGHTS.BULK_USER_ACCOUNT_CHANGE)
    ), [hasAccess]);
    const showUpdateLcdAnalysis = useMemo(() => (
        hasAccess(ACCESS_RIGHTS.LCD_CREDIT_ANALYSIS_BULK_UPDATE)
    ), [hasAccess]);

    const toolsCount = useMemo(() => [
        showUpdateGroup,
        showUpdateUserState,
        showUpdateUserNewsletter,
        showUpdateUserAccount,
        showUpdateLcdAnalysis,
    ].filter(Boolean).length, [
        showUpdateGroup,
        showUpdateUserState,
        showUpdateUserNewsletter,
        showUpdateUserAccount,
        showUpdateLcdAnalysis,
    ]);

    return (
        <>
            {toolsCount < 6 ? (
                <ToolsRow
                    userIds={ids as Array<number>}

                    showUpdateGroup={showUpdateGroup}
                    openAssignToGroup={openAssignToGroup}
                    openDischargeFromGroup={openDischargeToGroup}

                    showUpdateUserState={showUpdateUserState}
                    openUpdateUserStateTool={openUpdateUserStateTool}

                    showUpdateUserNewsletter={showUpdateUserNewsletter}
                    openUpdateUserNewsletterTool={openUpdateUserNewsletterTool}

                    showUpdateUserAccount={showUpdateUserAccount}
                    openUpdateUserAccountTool={openUpdateUserAccountTool}

                    showUpdateLcdAnalysis={showUpdateLcdAnalysis}
                    openSubscribeToLcdAnalysisTool={openSubscribeToLcdAnalysisTool}
                    openUnsubscribeFromLcdAnalysisTool={openUnsubscribeFromLcdAnalysisTool}
                />
            ) : (
                <ToolsMenu
                    userIds={ids as Array<number>}

                    showUpdateGroup={showUpdateGroup}
                    openAssignToGroup={openAssignToGroup}
                    openDischargeFromGroup={openDischargeToGroup}

                    showUpdateUserState={showUpdateUserState}
                    openUpdateUserStateTool={openUpdateUserStateTool}

                    showUpdateUserNewsletter={showUpdateUserNewsletter}
                    openUpdateUserNewsletterTool={openUpdateUserNewsletterTool}

                    showUpdateUserAccount={showUpdateUserAccount}
                    openUpdateUserAccountTool={openUpdateUserAccountTool}

                    showUpdateLcdAnalysis={showUpdateLcdAnalysis}
                    openSubscribeToLcdAnalysisTool={openSubscribeToLcdAnalysisTool}
                    openUnsubscribeFromLcdAnalysisTool={openUnsubscribeFromLcdAnalysisTool}
                />
            )}
            <AssignToGroupTool
                shown={assignToGroupShown}
                userIds={ids}
                accountIdsSelector={(state: AppState, userIds: number[]) => (
                    allSelectedUsersAccountsSelector(state, userIds)
                )}
                onDone={onDone}
                onClose={closeAssignToGroup}
            />
            <UnassignFromGroupTool
                shown={dischargeToGroupShown}
                userIds={ids}
                onDone={onDone}
                onClose={closeDischargeToGroup}
            />
            <UpdateUserStateTool
                shown={updateUserStateToolShown}
                userIds={ids}
                onDone={onDone}
                onClose={closeUpdateUserStateTool}
            />
            <UpdateUserNewsletterTool
                shown={updateUserNewsletterToolShown}
                userIds={ids}
                onDone={onDone}
                onClose={closeUpdateUserNewsletterTool}
            />
            <UpdateUserAccountTool
                shown={updateUserAccountToolShown}
                userIds={ids}
                onDone={onDone}
                onClose={closeUpdateUserAccountTool}
            />
            <SubscribeToLcdCreditAnalysisTool
                shown={subscribeToLcdAnalysisToolShown}
                usersIds={ids}
                onDone={onDone}
                onClose={closeSubscribeToLcdAnalysisTool}
            />
            <UnsubscribeFromLcdCreditAnalysisTool
                shown={unsubscribeFromLcdAnalysisToolShown}
                usersIds={ids}
                onDone={onDone}
                onClose={closeUnsubscribeFromLcdAnalysisTool}
            />
        </>
    );
};
